import { useState, useEffect } from "react";
import { Flex, Heading, Text } from "@chakra-ui/react";
import {
  newBorder,
  textPrimary,
  textSecondary,
  backgroundLight,
  color2,
} from "../../../utils/colors";
import { columnsDesktop } from "./TreatmentPlanServicesColumns"; // Adjust the path according to your file structure
import { columnsMobile } from "./TreatmentPlanServicesColumnsMobile"; // Adjust the path according to your file structure
import { useTranslation } from "react-i18next";
import useIsMobile from "../../../hooks/useIsMobile";
import TreatmentPlansServicesTablePublic from "./TreatmentPlansServicesTablePublic";
import useCurrencySymbol from "../../../hooks/useCurrencySymbol";

const TreatmentPlanServicesPublic = ({ treatmentPlan, currency }) => {
  // TRANSLATION
  const { t } = useTranslation();
  const translate = (key) => t(key);

  const [totalDiscount, setTotalDiscount] = useState(0);
  const [totalReimbursedAmount, setTotalReimbursedAmount] = useState(0);
  const [patientAmountToPay, setPatientAmountToPay] = useState(0);

  // MOBILE
  const isMobile = useIsMobile();

  useEffect(() => {
    const totalDiscountedPrice = treatmentPlan.services.reduce(
      (total, service) => {
        const { discountType, discountValue, price, quantity } = service;

        let discount = 0;

        if (discountType === "amount") {
          discount = discountValue * quantity;
        } else if (discountType === "percentage") {
          discount = price * (discountValue / 100) * quantity;
        } else {
          discount = 0;
        }

        return total + discount;
      },
      0
    );

    setTotalDiscount(totalDiscountedPrice);

    const totalReimbursed = treatmentPlan.services.reduce((total, service) => {
      const reimbursed = service.reimbursement?.reimbursedAmount || 0;
      return total + reimbursed; // ✅ no need to multiply
    }, 0);
    setTotalReimbursedAmount(totalReimbursed);

    const totalPatientAmountToPay = treatmentPlan.services.reduce(
      (total, service) => {
        const reimbursed = service.reimbursement?.reimbursedAmount || 0;
        const quantity = service.quantity || 1;
        const discountedPrice = service.discountedPrice || service.price; // fallback
        const subtotal = discountedPrice * quantity;
        return total + (subtotal - reimbursed);
      },
      0
    );

    setPatientAmountToPay(totalPatientAmountToPay);
  }, [treatmentPlan.services]);

  const currencySymbol = useCurrencySymbol(currency);

  // console.log("treatmentPlan", treatmentPlan);
  // console.log("totalReimbursedAmount", totalReimbursedAmount);
  // console.log("totalDiscount", totalDiscount);
  // console.log("patientAmountToPay", patientAmountToPay);

  const loading = false;
  const error = false;

  return (
    <Flex
      direction="column"
      w="100%"
      border={[`0px solid ${newBorder}`, `1px solid ${newBorder}`]}
      borderRadius={["0rem", "1rem"]}
      p="0 0 0rem"
    >
      <Heading
        fontSize={["0.875rem", "1rem", "xs"]}
        fontWeight="500"
        color={textPrimary}
        p={["0.75rem 1rem", "0.75rem 1rem"]}
        textAlign="left"
        bg={backgroundLight}
        borderBottom={`1px solid ${newBorder}`}
        borderTopLeftRadius={["0rem", "1rem"]}
        borderTopRightRadius={["0rem", "1rem"]}
      >
        {t("treatmentPlan.serviceList")}
      </Heading>

      <TreatmentPlansServicesTablePublic
        currencySymbol={currencySymbol}
        className="treatmentPlans-preview-table"
        columns={
          isMobile
            ? columnsMobile(translate, currencySymbol)
            : columnsDesktop(translate, currencySymbol)
        }
        data={treatmentPlan.services}
        loading={loading}
        error={error}
        // onRowClick={(userId) => {
        //   setIsSidePanelLongOpen(true);
        //   setUserId(userId);
        // }}
        // noItemsButtonHandler={openSidePanelModal}
        noItemsTitle={t("noItems.noPatients")}
        noItemsDescription={t("noItems.noPatientsText")}
        noItemsButtonLabel={t("noItems.noPatientsButton")}
        // noItemsButtonHandler={openCreatePatientSidePanelModal}
        isHeaderWhite
        onRowClick={(row) => console.log("row", row)}
      />

      <Flex
        w="100%"
        align="flex-start"
        justify="space-between"
        p={["1.5rem 1.5rem 1.5rem", "1.5rem 1.5rem 1.5rem"]}
        gap="2rem"
        direction={["column", "row"]}
      >
        <Flex w="100%">
          <Text fontSize="xs" fontWeight="500" color={textSecondary}>
            {t("treatmentPlan.notesContactText")}
          </Text>
        </Flex>
        <Flex w={["100%", "50%"]} justify="flex-end">
          <Flex direction="column" gap={1} w={["100%", "auto"]}>
            {/* SERVICE COUNT */}
            <Flex align="center" justify="space-between" whiteSpace="nowrap">
              <Text
                color={textSecondary}
                fontSize="xs"
                fontWeight="500"
                mr="2rem"
              >
                {t("service.serviceCount")}:
              </Text>
              <Text color={textPrimary} fontSize="xs" fontWeight="600">
                {treatmentPlan.services.length}
              </Text>
            </Flex>

            {/* DISCOUNT */}
            {totalDiscount > 0 && (
              <Flex
                align="center"
                justify="space-between"
                textAlign="right"
                whiteSpace="nowrap"
              >
                <Text
                  color={textSecondary}
                  fontSize="xs"
                  fontWeight="500"
                  mr="2rem"
                >
                  {t("service.discount")}:
                </Text>
                <Text color={textPrimary} fontSize="xs" fontWeight="600">
                  {currencySymbol} -{totalDiscount}
                </Text>
              </Flex>
            )}

            {/* TOTAL PRICE */}
            <Flex align="center" justify="space-between" whiteSpace="nowrap">
              <Text
                color={textSecondary}
                fontSize="xs"
                fontWeight="500"
                mr="2rem"
              >
                {t("treatmentPlan.priceTotal")}:
              </Text>
              <Text color={textPrimary} fontSize="xs" fontWeight="600">
                {currencySymbol} {treatmentPlan.totalPrice}
              </Text>
            </Flex>

            {/* REIMBURSEMENT */}
            {totalReimbursedAmount > 0 && (
              <>
                <Flex
                  align="center"
                  justify="space-between"
                  textAlign="right"
                  whiteSpace="nowrap"
                >
                  <Text
                    color={textSecondary}
                    fontSize="xs"
                    fontWeight="500"
                    mr="2rem"
                  >
                    {t("service.reimbursedAmount")}:
                  </Text>
                  <Text color={color2} fontSize="xs" fontWeight="600">
                    {currencySymbol} {totalReimbursedAmount}
                  </Text>
                </Flex>

                <Flex
                  align="center"
                  justify="space-between"
                  textAlign="right"
                  whiteSpace="nowrap"
                >
                  <Text
                    color={textSecondary}
                    fontSize="xs"
                    fontWeight="500"
                    mr="2rem"
                  >
                    {t("service.patientAmountToPay")}:
                  </Text>
                  <Text color={textPrimary} fontSize="xs" fontWeight="600">
                    {currencySymbol} {patientAmountToPay}
                  </Text>
                </Flex>
              </>
            )}
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
};

export default TreatmentPlanServicesPublic;
